import { ListaPlanos, LoadingComponent, MasterPage } from "@components";
import { PlanoVinculadoEntidade } from "@entidades";
import { DateFix } from "@helpers";
import { Alert, Box, Button, Col, ComboBox, Form, Input, Row } from "@intech/web-ui";
import { FichaContribPrevidencialService, PlanoVinculadoService } from "@services";
import { format, isAfter, isValid, parse } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";
import { FaDownload } from "react-icons/fa";

const ComboPeriodo = [
  {
    Key: 999,
    Name: "TODOS OS MESES"
  },
  {
    Key: 3,
    Name: "3 MESES"
  },
  {
    Key: 6,
    Name: "6 MESES"
  },
  {
    Key: 12,
    Name: "12 MESES"
  }
];

export const Extrato: React.FC = ({ ...rest }) => {
  const [Planos, setPlanos] = useState<PlanoVinculadoEntidade[]>([]);
  const [PlanoSelecionado, setPlanoSelecionado] = useState<PlanoVinculadoEntidade>(new PlanoVinculadoEntidade());
  const [SqPlanoVinculado, setSqPlanoVinculado] = useState<number>(0);
  const [Referencia, setReferencia] = useState<string>("");
  const [ReferenciaMax, setReferenciaMax] = useState<string>("");
  const [Periodo, setPeriodo] = useState<number>(999);

  const [Loading, setLoading] = useState<boolean>(true);
  const [Erro, setErro] = useState<string>("");
  const [FormErro, setFormErro] = useState<string>("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const planos = await PlanoVinculadoService.Buscar();

        if (planos.length > 0) {
          const plano = planos[0];

          setPlanoSelecionado(plano);
          setSqPlanoVinculado(plano.SQ_PLANO_PREVIDENCIAL);

          const ultimaReferencia = await FichaContribPrevidencialService.BuscarUltimaReferenciaPorPlano(plano.SQ_PLANO_PREVIDENCIAL);
          const referencia = DateFix(ultimaReferencia.DT_REFERENCIA);
          const referenciaMax = DateFix(ultimaReferencia.DT_REFERENCIA);

          setReferencia(referencia);
          setReferenciaMax(referenciaMax);
        }

        setPlanos(planos);
      } catch (e) {
        const err: any = e;
        setErro(err.response ? err.response.data : err);
      }
      finally {
        setLoading(false);
      }
    })();
  }, []);


  const handleSelecionarPlano = useCallback(async (planoSelecionado: PlanoVinculadoEntidade) => {
    try {
      const plano = Planos.filter(x => x.SQ_PLANO_PREVIDENCIAL === planoSelecionado.SQ_PLANO_PREVIDENCIAL)[0];

      if (plano) {
        setPlanoSelecionado(plano);
        setSqPlanoVinculado(plano.SQ_PLANO_PREVIDENCIAL);

        const ultimaReferencia = await FichaContribPrevidencialService.BuscarUltimaReferenciaPorPlano(plano.SQ_PLANO_PREVIDENCIAL);
        const referencia = DateFix(ultimaReferencia.DT_REFERENCIA);
        const referenciaMax = DateFix(ultimaReferencia.DT_REFERENCIA);

        setReferencia(referencia);
        setReferenciaMax(referenciaMax);
      }
      else {
        setErro("Houve uma falha ao selecionar o plano desejado.");
      }

    } catch (e) {
      const err: any = e;
      setErro(err.response ? err.response.data : err);
    }
  }, [Planos]);

  const GerarExtrato = useCallback(async () => {
    setFormErro("");
    try {
      const input = parse(Referencia, "dd/MM/yyyy", new Date());
      const max = parse(ReferenciaMax, "MM/yyyy", new Date());

      if (!isValid(input)) {
        setFormErro("Data inválida.");
        return;
      }

      const DataMaiorMax = isAfter(input, max);

      if (DataMaiorMax) {
        setFormErro(`A data informada não pode ser maior que a data de referência "${ReferenciaMax}`);
        return;
      }

      const referencia = format(input, "MM.yyyy");

      const resultado = await PlanoVinculadoService.GerarExtrato(PlanoSelecionado.SQ_PLANO_PREVIDENCIAL, Periodo, referencia);
      const name = "extrato de contribuições.pdf";
      const UrlResultado = resultado.UrlResultado;

      if (navigator.msSaveBlob) {
        // IE10+ : (has Blob, but not a[download] or URL)
        return navigator.msSaveBlob(new Blob([UrlResultado]), name);
      } else {
        const link = document.createElement("a");
        link.href = UrlResultado;
        link.setAttribute("download", name);
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);

        link.click();
      }
    } catch (e) {
      const err = e["response"] ? e["response"]["data"] : e;
      let erro = err;
      if (typeof err === "object" && err.text) {
        erro = await err.text();
      }
      setFormErro(erro);
    }
  }, [Referencia, ReferenciaMax, PlanoSelecionado, Periodo]);

  return (
    <MasterPage {...rest}>
      {Loading && <LoadingComponent />}

      {!Loading && Erro && <Alert type={"danger"}>{Erro}</Alert>}

      {!Loading && !Erro && Planos.length === 0 && <Alert type={"info"}>{"Nenhuma informação disponível no momento."}</Alert>}

      {!Loading && !Erro && Planos.length > 0 &&
        <>
          {Planos.length > 1 && <Row>
            <Col>
              <ListaPlanos planos={Planos} sqPlanoAtivo={SqPlanoVinculado} onChange={handleSelecionarPlano} />
            </Col>
          </Row>
          }

          <Row>
            <Col>
              <Box title={"Extrato de Contribuições"}>
                <Form>
                  <Input
                    value={Referencia}
                    title={"Referência"}
                    mask={"99/9999"}
                  />

                  <ComboBox
                    title={"Período"}
                    value={Periodo}
                    onChange={setPeriodo}
                    defaultValue={Periodo}
                    data={ComboPeriodo}
                    memberName={"Name"}
                    memberValue={"Key"}
                  />

                  <Alert type={"danger"}>{FormErro}</Alert>

                  <Button
                    title={"Gerar"}
                    type={"primary"}
                    onClick={GerarExtrato}
                    loadingText={"Gerando..."}
                    icon={<FaDownload />}
                    submit
                    usesLoading
                  />

                  <Alert className={'mt-4'} type={"info"}>{"Caso o extrato não seja exibido, verifique se seu navegador está bloqueando pop-ups."}</Alert>
                </Form>
              </Box>
            </Col>
          </Row>
        </>}
    </MasterPage>
  );
}
